import React, { useState, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sparkles, Send, Loader2, Mic, Video, Type, SkipForward, ArrowLeft, Check,
  BookOpen, Mail, Map, Mountain, Quote, Heart, FileText, Square, RotateCcw, Users, Clock,
  Lock, ChevronDown, Crown, Search, Play, Pause, MessageCircle, MapPin,
} from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl, formatApiError } from "../lib/api";
import { LocationAutocomplete } from "./LocationAutocomplete";
import { EmbeddedCheckoutInline } from "./EmbeddedCheckoutInline";
import { MoveToVaultButton } from "./MoveToVault";
import { useAuth } from "../context/AuthContext";

// Cross-browser MediaRecorder mime negotiation (Safari/iOS produce mp4, Chrome webm)
function pickMime(kind) {
  const cands = kind === "video"
    ? ["video/webm;codecs=vp9,opus", "video/webm;codecs=vp8,opus", "video/webm", "video/mp4"]
    : ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/mpeg"];
  const supported = typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported;
  for (const c of cands) { if (!supported || MediaRecorder.isTypeSupported(c)) return c; }
  return "";
}
function extFor(mime, kind) {
  if (!mime) return kind === "video" ? "mp4" : "m4a";
  if (mime.includes("mp4")) return kind === "video" ? "mp4" : "m4a";
  if (mime.includes("mpeg")) return "mp3";
  return kind === "video" ? "webm" : "webm";
}
function makeRecorder(stream, kind) {
  const mime = pickMime(kind);
  let mr;
  try { mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream); }
  catch { mr = new MediaRecorder(stream); }
  return mr;
}


const MANUAL_MODES = [
  { key: "interview", name: "Interviews", icon: BookOpen, live: true },
  { key: "letters", name: "Letters to the Future", icon: Mail, live: true },
  { key: "experience", name: "Experience Map", icon: Map, live: true },
  { key: "challenges", name: "Life Challenges", icon: Mountain, live: true },
  { key: "wisdom", name: "Words of Wisdom", icon: Quote, live: true },
  { key: "favorites", name: "Favorite Things", icon: Heart, live: true },
  { key: "greeting", name: "Greetings", icon: MessageCircle, live: true },
  { key: "legacy", name: "Legacy Materials", icon: FileText },
];
const SUBTOPICS = ["Early Childhood", "Relationship with Parents", "Love Affairs", "Military Service", "Career Experiences", "Faith & Religion", "Funny Stories"];

export function TimeCapsule({ viewUserId, viewUserName, feed }) {
  const [screen, setScreen] = useState("home");
  const [capsuleMode, setCapsuleMode] = useState("guide");

  if (viewUserId) return <CapsuleFeed lockedUserId={viewUserId} lockedUserName={viewUserName} />;
  if (feed) return <CapsuleFeed />;

  const startMode = (key) => {
    if (key === "interview") setScreen({ kind: "interview-topic" });
    else if (key === "legacy") toast("Legacy Materials", { description: "Coming soon." });
    else setScreen(capsuleMode === "guide" ? { kind: "modechat", mode: key } : { kind: "manual", mode: key });
  };
  const back = () => setScreen("home");

  return (
    <div className="flex flex-col h-full" data-testid="timecapsule-root">
      {screen === "home" && (
        <>
          <div className="flex rounded-full p-1 mb-4 shrink-0" style={{ background: "var(--overlay)", border: "1px solid var(--border)" }}>
            {[["guide", "Guide Me"], ["manual", "Manual"]].map(([k, label]) => (
              <button key={k} onClick={() => setCapsuleMode(k)} data-testid={`tc-mode-${k}`}
                className="flex-1 rounded-full py-2 text-sm font-medium transition-colors"
                style={capsuleMode === k ? { background: "var(--accent)", color: "#fff" } : { color: "var(--text-secondary)" }}>
                {label}
              </button>
            ))}
          </div>
          {capsuleMode === "guide"
            ? <GuideChat onStart={startMode} />
            : <ManualModes onPick={startMode} />}
        </>
      )}
      {screen?.kind === "interview-topic" && <TopicPicker setScreen={setScreen} />}
      {screen?.kind === "interview" && <Interview topic={screen.topic} onExit={back} />}
      {screen?.kind === "modechat" && <ModeChat mode={screen.mode} onExit={back} />}
      {screen?.kind === "manual" && <ManualForm mode={screen.mode} onExit={back} />}
    </div>
  );
}

const LIVE_MODES = [["interview", "Interviews"], ["letters", "Letters"], ["experience", "Experience"], ["challenges", "Challenges"], ["wisdom", "Wisdom"], ["favorites", "Favorites"], ["greeting", "Greetings"]];

function GuideChat({ onStart }) {
  const [messages, setMessages] = useState([{ role: "assistant", content: "Hi! I'm your Time Capsule guide. What part of your life would you like to preserve for your family?" }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [showStarts, setShowStarts] = useState(false);
  const endRef = useRef();
  useEffect(() => { endRef.current?.scrollIntoView({ block: "nearest" }); }, [messages]);

  const send = async () => {
    if (!input.trim() || busy) return;
    const msg = input.trim();
    const hist = [...messages, { role: "user", content: msg }];
    setMessages(hist); setInput(""); setBusy(true);
    try {
      const { data } = await api.post("/timecapsule/guide", { message: msg, history: messages });
      setMessages([...hist, { role: "assistant", content: data.reply }]);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar space-y-3 pb-2" data-testid="tc-guide-chat">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className="max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm whitespace-pre-wrap"
              style={m.role === "user" ? { background: "var(--accent)", color: "#fff" } : { background: "var(--overlay)", color: "var(--text-primary)" }}>
              {m.content}
            </div>
          </div>
        ))}
        {busy && <div className="flex justify-start"><div className="rounded-2xl px-3.5 py-2.5" style={{ background: "var(--overlay)" }}><Loader2 size={16} className="animate-spin gold-text" /></div></div>}
        <div ref={endRef} />
      </div>
      <button onClick={() => setShowStarts((s) => !s)} data-testid="tc-guide-start-interview"
        className="shrink-0 w-full rounded-xl py-3 my-2 text-sm font-semibold flex items-center justify-center gap-2"
        style={{ background: "color-mix(in srgb, var(--accent) 16%, transparent)", color: "var(--accent)" }}>
        <Sparkles size={15} /> Start a mode
      </button>
      {showStarts && (
        <div className="shrink-0 flex flex-wrap gap-2 mb-2" data-testid="tc-guide-starts">
          {LIVE_MODES.map(([k, label]) => (
            <button key={k} onClick={() => onStart(k)} data-testid={`tc-guide-start-${k}`}
              className="rounded-full px-3 py-1.5 text-xs font-medium card-surface">{label}</button>
          ))}
        </div>
      )}
      <div className="shrink-0 flex items-center gap-2">
        <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Tell me what's on your mind…" data-testid="tc-guide-input"
          className="flex-1 rounded-full px-4 py-3 text-sm outline-none"
          style={{ background: "var(--bg-surface)", border: "1px solid var(--border)", color: "var(--text-primary)" }} />
        <button onClick={send} disabled={busy} data-testid="tc-guide-send"
          className="h-11 w-11 rounded-full grid place-items-center shrink-0" style={{ background: "var(--accent)", color: "#fff" }}>
          <Send size={18} />
        </button>
      </div>
    </div>
  );
}

function ManualModes({ onPick }) {
  return (
    <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar grid grid-cols-2 gap-3 pb-2" data-testid="tc-manual-modes">
      {MANUAL_MODES.map((m) => (
        <motion.button key={m.key} whileTap={{ scale: 0.96 }}
          onClick={() => m.live ? onPick(m.key) : toast(m.name, { description: "Coming in a future update." })}
          data-testid={`tc-manual-${m.key}`}
          className="rounded-2xl p-4 flex flex-col items-start gap-2 card-surface" style={{ minHeight: 100 }}>
          <div className="h-9 w-9 rounded-xl grid place-items-center" style={{ background: "color-mix(in srgb, var(--accent) 14%, transparent)" }}>
            <m.icon size={18} className="gold-text" />
          </div>
          <span className="font-serif-display text-[15px] leading-tight text-left">{m.name}</span>
          {!m.live && <span className="text-[10px] uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>Coming soon</span>}
        </motion.button>
      ))}
    </div>
  );
}

function TopicPicker({ setScreen }) {
  return (
    <div className="flex flex-col flex-1 min-h-0">
      <button onClick={() => setScreen("home")} className="shrink-0 flex items-center gap-2 text-sm mb-3" data-testid="tc-topic-back" style={{ color: "var(--text-secondary)" }}>
        <ArrowLeft size={16} /> Back
      </button>
      <div className="font-serif-display text-2xl mb-1 shrink-0">Choose a topic</div>
      <p className="text-sm mb-4 shrink-0" style={{ color: "var(--text-secondary)" }}>The AI will ask you 10 questions on the topic you pick.</p>
      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar space-y-2 pb-2">
        {SUBTOPICS.map((t) => (
          <motion.button key={t} whileTap={{ scale: 0.98 }} onClick={() => setScreen({ kind: "interview", topic: t })}
            data-testid={`tc-topic-${t.replace(/\s+/g, "-").toLowerCase()}`}
            className="w-full text-left rounded-xl px-4 py-3.5 card-surface text-sm font-medium">
            {t}
          </motion.button>
        ))}
      </div>
    </div>
  );
}

function Interview({ topic, onExit }) {
  const [loading, setLoading] = useState(true);
  const [questions, setQuestions] = useState([]);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [tab, setTab] = useState("text");
  const [text, setText] = useState("");
  const [draft, setDraft] = useState(null); // {type, media}
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  // recording
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const loadQuestions = useCallback(async () => {
    setLoading(true); setDone(false); setIdx(0); setAnswers([]); setText(""); setDraft(null); setTab("text");
    try {
      const { data } = await api.post("/timecapsule/interview/questions", { topic });
      setQuestions(data.questions);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); onExit(); }
    finally { setLoading(false); }
  }, [topic, onExit]);
  useEffect(() => { loadQuestions(); }, [loadQuestions]);

  const stopStream = () => { try { recorderRef.current?.stream?.getTracks().forEach((t) => t.stop()); } catch {} };

  const startRec = async (kind) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia(kind === "video" ? { video: true, audio: true } : { audio: true });
      const mr = makeRecorder(stream, kind);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        clearInterval(timerRef.current);
        stream.getTracks().forEach((t) => t.stop());
        const actual = mr.mimeType || (kind === "video" ? "video/mp4" : "audio/mp4");
        const blob = new Blob(chunksRef.current, { type: actual });
        if (!blob.size) { toast.error("Nothing was recorded — please try again"); return; }
        try {
          const fd = new FormData();
          fd.append("file", new File([blob], `answer.${extFor(actual, kind)}`, { type: actual }));
          const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
          setDraft({ type: kind, media: data.path });
          toast.success(kind === "video" ? "Video saved" : "Recording saved");
        } catch { toast.error("Upload failed"); }
      };
      recorderRef.current = mr;
      mr.start();
      setRecording(true); setSeconds(0);
      timerRef.current = setInterval(() => setSeconds((s) => {
        const n = s + 1;
        if (kind === "video" && n >= 30) stopRec();
        return n;
      }), 1000);
    } catch { toast.error("Couldn't access your microphone/camera"); }
  };
  const stopRec = () => {
    if (recorderRef.current && recorderRef.current.state !== "inactive") recorderRef.current.stop();
    setRecording(false);
    clearInterval(timerRef.current);
  };
  useEffect(() => () => { clearInterval(timerRef.current); stopStream(); }, []);

  const canProceed = (tab === "text" && text.trim()) || draft;

  const next = (skip = false) => {
    const q = questions[idx];
    let ans;
    if (skip) ans = { question: q, type: "skipped", text: null, media: null };
    else if (draft) ans = { question: q, type: draft.type, media: draft.media, text: null };
    else ans = { question: q, type: "text", text: text.trim(), media: null };
    const nextAnswers = [...answers, ans];
    setAnswers(nextAnswers);
    setText(""); setDraft(null); setTab("text"); setSeconds(0);
    if (idx + 1 >= questions.length) save(nextAnswers);
    else setIdx(idx + 1);
  };

  const save = async (finalAnswers) => {
    setSaving(true);
    try {
      await api.post("/timecapsule/interview/save", { topic, answers: finalAnswers });
      setDone(true);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (loading) return (
    <div className="grid place-items-center flex-1">
      <div className="text-center">
        <Loader2 size={32} className="animate-spin gold-text mx-auto" />
        <p className="text-sm mt-3" style={{ color: "var(--text-secondary)" }}>Preparing your {topic} interview…</p>
      </div>
    </div>
  );

  if (saving) return <div className="grid place-items-center flex-1"><Loader2 size={32} className="animate-spin gold-text" /></div>;

  if (done) return (
    <div className="grid place-items-center flex-1 text-center" data-testid="tc-interview-done">
      <div>
        <Check size={52} className="mx-auto gold-text" />
        <div className="font-serif-display text-3xl mt-3">Sealed in your capsule</div>
        <p className="text-sm mt-2" style={{ color: "var(--text-secondary)" }}>Your {topic} interview is saved.</p>
        <div className="mt-8 space-y-2">
          <button onClick={loadQuestions} data-testid="tc-another-interview"
            className="w-full rounded-xl py-3 text-sm font-semibold" style={{ background: "var(--accent)", color: "#fff" }}>
            Conduct another interview
          </button>
          <button onClick={onExit} data-testid="tc-switch-modes"
            className="w-full rounded-xl py-3 text-sm" style={{ background: "var(--overlay)", color: "var(--text-secondary)" }}>
            Switch modes
          </button>
        </div>
      </div>
    </div>
  );

  const q = questions[idx];
  const pct = Math.round(((idx + 1) / questions.length) * 100);

  return (
    <div className="flex flex-col flex-1 min-h-0" data-testid="tc-interview">
      <div className="shrink-0 mb-3">
        <div className="flex items-center justify-between text-xs mb-1.5" style={{ color: "var(--text-muted)" }}>
          <span>{topic}</span><span data-testid="tc-progress-text">Question {idx + 1} of {questions.length}</span>
        </div>
        <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "var(--overlay)" }}>
          <motion.div className="h-full" style={{ background: "var(--accent)" }} animate={{ width: `${pct}%` }} data-testid="tc-progress-bar" />
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar">
        <div className="font-serif-display text-2xl leading-snug mb-4" data-testid="tc-question">{q}</div>

        <div className="flex gap-2 mb-3">
          {[["text", Type], ["audio", Mic], ["video", Video]].map(([k, Icon]) => (
            <button key={k} onClick={() => { if (recording) return; setTab(k); setDraft(null); }} data-testid={`tc-tab-${k}`}
              className="flex items-center gap-1.5 rounded-full px-3.5 py-2 text-xs font-medium"
              style={tab === k ? { background: "var(--accent)", color: "#fff" } : { background: "var(--overlay)", color: "var(--text-secondary)", border: "1px solid var(--border)" }}>
              <Icon size={14} /> {k === "text" ? "Type" : k === "audio" ? "Voice" : "Video"}
            </button>
          ))}
        </div>

        {tab === "text" && (
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={6} placeholder="Share your story…"
            data-testid="tc-answer-text"
            className="w-full rounded-xl px-4 py-3 text-sm outline-none"
            style={{ background: "var(--bg-surface)", border: "1px solid var(--border)", color: "var(--text-primary)" }} />
        )}

        {(tab === "audio" || tab === "video") && (
          <div className="rounded-xl p-5 text-center" style={{ background: "var(--overlay)" }}>
            {draft ? (
              <div>
                {draft.type === "video"
                  ? <video src={fileUrl(draft.media)} controls className="w-full rounded-lg max-h-56" />
                  : <audio src={fileUrl(draft.media)} controls className="w-full" />}
                <button onClick={() => setDraft(null)} data-testid="tc-rerecord"
                  className="mt-3 inline-flex items-center gap-1.5 text-sm" style={{ color: "var(--accent)" }}>
                  <RotateCcw size={14} /> Re-record
                </button>
              </div>
            ) : recording ? (
              <div>
                <div className="text-3xl font-serif-display gold-text tabular-nums">{String(Math.floor(seconds / 60)).padStart(2, "0")}:{String(seconds % 60).padStart(2, "0")}</div>
                <p className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>{tab === "video" ? "Recording (max 30s)…" : "Recording…"}</p>
                <button onClick={stopRec} data-testid="tc-stop-rec"
                  className="mt-4 h-14 w-14 rounded-full grid place-items-center mx-auto" style={{ background: "var(--destructive)" }}>
                  <Square size={20} fill="#fff" color="#fff" />
                </button>
              </div>
            ) : (
              <div>
                {tab === "video" ? <Video size={28} className="mx-auto gold-text" /> : <Mic size={28} className="mx-auto gold-text" />}
                <p className="text-xs mt-2 mb-4" style={{ color: "var(--text-muted)" }}>
                  {tab === "video" ? "Record up to a 30-second video answer" : "Record a voice answer"}
                </p>
                <button onClick={() => startRec(tab)} data-testid="tc-start-rec"
                  className="h-14 w-14 rounded-full grid place-items-center mx-auto" style={{ background: "var(--accent)" }}>
                  {tab === "video" ? <Video size={22} color="#fff" /> : <Mic size={22} color="#fff" />}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="shrink-0 flex items-center gap-2 pt-3">
        <button onClick={() => next(true)} data-testid="tc-skip"
          className="flex items-center gap-1.5 rounded-xl px-4 py-3 text-sm" style={{ background: "var(--overlay)", color: "var(--text-secondary)" }}>
          <SkipForward size={15} /> Skip
        </button>
        <button onClick={() => next(false)} disabled={!canProceed || recording} data-testid="tc-next"
          className="flex-1 rounded-xl py-3 text-sm font-semibold flex items-center justify-center gap-2"
          style={{ background: canProceed && !recording ? "var(--accent)" : "var(--overlay)", color: canProceed && !recording ? "#fff" : "var(--text-muted)" }}>
          {idx + 1 >= questions.length ? "Finish & Save" : "Next"}
        </button>
      </div>
    </div>
  );
}

const MODE_LABEL = { letters: "Letters to the Future", experience: "Experience Map", challenges: "Life Challenges", wisdom: "Words of Wisdom", favorites: "Favorite Things", greeting: "Greeting" };
const MODE_GREETING = {
  letters: "Who would you like to write to in the future, and what's on your heart?",
  experience: "Tell me about an experience you'd like to remember — where were you and what happened?",
  challenges: "What challenge are you facing, or have you faced? Share what's on your mind.",
  wisdom: "What piece of wisdom would you like to pass on to your family?",
  favorites: "Tell me about something you love — what is it?",
  greeting: "Who's this greeting for, and what would you like to say to them?",
};

function ModeChat({ mode, onExit }) {
  const [messages, setMessages] = useState([{ role: "assistant", content: MODE_GREETING[mode] || "Let's begin." }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const endRef = useRef();
  useEffect(() => { endRef.current?.scrollIntoView({ block: "nearest" }); }, [messages, draft]);

  const send = async () => {
    if (!input.trim() || busy) return;
    const msg = input.trim();
    const hist = [...messages, { role: "user", content: msg }];
    setMessages(hist); setInput(""); setBusy(true);
    try {
      const { data } = await api.post("/timecapsule/chat", { mode, message: msg, history: messages });
      setMessages([...hist, { role: "assistant", content: data.reply }]);
      if (data.draft) setDraft(data.draft);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/timecapsule/entry", { mode, title: draft.title || MODE_LABEL[mode], data: draft });
      setDone(true);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (done) return <DoneScreen label={MODE_LABEL[mode]} onExit={onExit} />;

  return (
    <div className="flex flex-col flex-1 min-h-0" data-testid={`tc-modechat-${mode}`}>
      <button onClick={onExit} className="shrink-0 flex items-center gap-2 text-sm mb-2" data-testid="tc-modechat-back" style={{ color: "var(--text-secondary)" }}><ArrowLeft size={16} /> Back</button>
      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar space-y-3 pb-2">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className="max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm whitespace-pre-wrap"
              style={m.role === "user" ? { background: "var(--accent)", color: "#fff" } : { background: "var(--overlay)", color: "var(--text-primary)" }}>{m.content}</div>
          </div>
        ))}
        {busy && <div className="flex justify-start"><div className="rounded-2xl px-3.5 py-2.5" style={{ background: "var(--overlay)" }}><Loader2 size={16} className="animate-spin gold-text" /></div></div>}
        {draft && (
          <div className="rounded-2xl p-4 card-surface" data-testid="tc-modechat-draft" style={{ border: "1px solid color-mix(in srgb, var(--accent) 40%, transparent)" }}>
            <div className="text-[10px] uppercase tracking-widest gold-text mb-2">Ready to save</div>
            {Object.entries(draft).map(([k, v]) => k === "title" ? null : (
              <div key={k} className="mb-2">
                <div className="text-[11px] uppercase" style={{ color: "var(--text-muted)" }}>{k}</div>
                <div className="text-sm whitespace-pre-wrap">{String(v)}</div>
              </div>
            ))}
            <div className="flex gap-2 mt-2">
              <button onClick={save} disabled={saving} data-testid="tc-modechat-save"
                className="flex-1 rounded-xl py-2.5 text-sm font-semibold" style={{ background: "var(--accent)", color: "#fff" }}>
                {saving ? "Saving…" : "Save to capsule"}
              </button>
              <button onClick={() => setDraft(null)} data-testid="tc-modechat-edit"
                className="rounded-xl px-4 py-2.5 text-sm" style={{ background: "var(--overlay)", color: "var(--text-secondary)" }}>Keep editing</button>
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>
      <div className="shrink-0 flex items-center gap-2 pt-1">
        <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Type your reply…" data-testid="tc-modechat-input"
          className="flex-1 rounded-full px-4 py-3 text-sm outline-none" style={{ background: "var(--bg-surface)", border: "1px solid var(--border)", color: "var(--text-primary)" }} />
        <button onClick={send} disabled={busy} data-testid="tc-modechat-send" className="h-11 w-11 rounded-full grid place-items-center shrink-0" style={{ background: "var(--accent)", color: "#fff" }}><Send size={18} /></button>
      </div>
    </div>
  );
}

function DoneScreen({ label, onExit }) {
  return (
    <div className="grid place-items-center flex-1 text-center" data-testid="tc-mode-done">
      <div>
        <Check size={52} className="mx-auto gold-text" />
        <div className="font-serif-display text-3xl mt-3">Saved to your capsule</div>
        <p className="text-sm mt-2" style={{ color: "var(--text-secondary)" }}>Your {label} is safely stored.</p>
        <button onClick={onExit} data-testid="tc-mode-done-back" className="mt-8 w-full rounded-xl py-3 text-sm font-semibold" style={{ background: "var(--accent)", color: "#fff" }}>Done</button>
      </div>
    </div>
  );
}

function MediaAnswer({ capSeconds = 60, onChange, placeholder }) {
  const [tab, setTab] = useState("text");
  const [text, setText] = useState("");
  const [draft, setDraft] = useState(null);
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const recorderRef = useRef(null); const chunksRef = useRef([]); const timerRef = useRef(null);
  useEffect(() => () => { clearInterval(timerRef.current); try { recorderRef.current?.stream?.getTracks().forEach((t) => t.stop()); } catch {} }, []);

  const emit = (t, tx, media) => onChange(tx || media ? { type: t, text: tx || null, media: media || null } : null);

  const start = async (kind) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia(kind === "video" ? { video: true, audio: true } : { audio: true });
      const mr = makeRecorder(stream, kind); chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        clearInterval(timerRef.current); stream.getTracks().forEach((t) => t.stop());
        const actual = mr.mimeType || (kind === "video" ? "video/mp4" : "audio/mp4");
        const blob = new Blob(chunksRef.current, { type: actual });
        if (!blob.size) { toast.error("Nothing was recorded — please try again"); return; }
        try {
          const fd = new FormData(); fd.append("file", new File([blob], `answer.${extFor(actual, kind)}`, { type: actual }));
          const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
          setDraft({ type: kind, media: data.path }); emit(kind, null, data.path); toast.success("Saved");
        } catch { toast.error("Upload failed"); }
      };
      recorderRef.current = mr; mr.start(); setRecording(true); setSeconds(0);
      timerRef.current = setInterval(() => setSeconds((s) => { const n = s + 1; if (n >= capSeconds) stop(); return n; }), 1000);
    } catch { toast.error("Couldn't access mic/camera"); }
  };
  const stop = () => { if (recorderRef.current && recorderRef.current.state !== "inactive") recorderRef.current.stop(); setRecording(false); clearInterval(timerRef.current); };

  return (
    <div>
      <div className="flex gap-2 mb-2">
        {[["text", Type, "Type"], ["audio", Mic, "Voice"], ["video", Video, "Video"]].map(([k, Icon, lbl]) => (
          <button key={k} onClick={() => { if (recording) return; setTab(k); setDraft(null); emit("text", "", null); if (k === "text") emit("text", text, null); }}
            data-testid={`tc-media-tab-${k}`}
            className="flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium"
            style={tab === k ? { background: "var(--accent)", color: "#fff" } : { background: "var(--overlay)", color: "var(--text-secondary)", border: "1px solid var(--border)" }}>
            <Icon size={13} /> {lbl}
          </button>
        ))}
      </div>
      {tab === "text" && (
        <textarea value={text} onChange={(e) => { setText(e.target.value); emit("text", e.target.value, null); }} rows={3} placeholder={placeholder || "Type here…"}
          className="w-full rounded-xl px-3 py-2.5 text-sm outline-none" style={{ background: "var(--bg-surface)", border: "1px solid var(--border)", color: "var(--text-primary)" }} />
      )}
      {(tab === "audio" || tab === "video") && (
        <div className="rounded-xl p-4 text-center" style={{ background: "var(--overlay)" }}>
          {draft ? (
            <div>
              {draft.type === "video" ? <video src={fileUrl(draft.media)} controls className="w-full rounded-lg max-h-48" /> : <audio src={fileUrl(draft.media)} controls className="w-full" />}
              <button onClick={() => { setDraft(null); emit("text", "", null); }} className="mt-2 inline-flex items-center gap-1.5 text-sm" style={{ color: "var(--accent)" }}><RotateCcw size={14} /> Re-record</button>
            </div>
          ) : recording ? (
            <div>
              <div className="text-2xl font-serif-display gold-text tabular-nums">{String(Math.floor(seconds / 60)).padStart(2, "0")}:{String(seconds % 60).padStart(2, "0")}</div>
              <p className="text-[11px] mt-1" style={{ color: "var(--text-muted)" }}>Recording (max {capSeconds}s)…</p>
              <button onClick={stop} className="mt-3 h-12 w-12 rounded-full grid place-items-center mx-auto" style={{ background: "var(--destructive)" }}><Square size={18} fill="#fff" color="#fff" /></button>
            </div>
          ) : (
            <button onClick={() => start(tab)} className="h-12 w-12 rounded-full grid place-items-center mx-auto" style={{ background: "var(--accent)" }}>
              {tab === "video" ? <Video size={20} color="#fff" /> : <Mic size={20} color="#fff" />}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function ManualForm({ mode, onExit }) {
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [fields, setFields] = useState({});
  const set = (k, v) => setFields((f) => ({ ...f, [k]: v }));

  const cfg = {
    letters: { title: "Letters to the Future", parts: [] },
    experience: { title: "Experience Map", parts: [] },
    greeting: { title: "Greeting" },
    challenges: { title: "Life Challenges", cap: 60, qs: [["situation", "What's happening?"], ["feelings", "How do you feel?"], ["lessons", "What have you learned, or are you learning?"]] },
    wisdom: { title: "Words of Wisdom", cap: 60, qs: [["wisdom", "What is the wisdom you want to pass on?"], ["circumstance", "What life circumstance led you to that wisdom?"]] },
    favorites: { title: "Favorite Things", cap: 60, qs: [["thing", "Describe a favorite thing"], ["why", "Why is it a favorite?"]] },
  }[mode];

  const save = async (data) => {
    setSaving(true);
    try { await api.post("/timecapsule/entry", { mode, title: cfg.title, data }); setDone(true); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (done) return <DoneScreen label={cfg.title} onExit={onExit} />;
  if (saving) return <div className="grid place-items-center flex-1"><Loader2 size={30} className="animate-spin gold-text" /></div>;

  const Header = () => (
    <div className="shrink-0 mb-3">
      <button onClick={onExit} className="flex items-center gap-2 text-sm mb-2" data-testid="tc-manualform-back" style={{ color: "var(--text-secondary)" }}><ArrowLeft size={16} /> Back</button>
      <div className="font-serif-display text-2xl">{cfg.title}</div>
    </div>
  );

  if (mode === "letters") {
    return (
      <div className="flex flex-col flex-1 min-h-0" data-testid="tc-manualform-letters">
        <Header />
        <input placeholder="Title (optional)" onChange={(e) => set("title", e.target.value)} data-testid="tc-letter-title"
          className="shrink-0 w-full rounded-xl px-4 py-3 mb-2 text-sm outline-none" style={{ background: "var(--bg-surface)", border: "1px solid var(--border)", color: "var(--text-primary)" }} />
        <textarea placeholder="Dear future…" onChange={(e) => set("letter", e.target.value)} data-testid="tc-letter-body"
          className="flex-1 min-h-0 w-full rounded-xl px-4 py-3 text-sm outline-none resize-none" style={{ background: "var(--bg-surface)", border: "1px solid var(--border)", color: "var(--text-primary)", lineHeight: 1.8 }} />
        <button onClick={() => save({ title: fields.title || "", letter: fields.letter || "" })} disabled={!fields.letter} data-testid="tc-manual-save"
          className="shrink-0 w-full rounded-xl py-3 mt-3 text-sm font-semibold" style={{ background: fields.letter ? "var(--accent)" : "var(--overlay)", color: fields.letter ? "#fff" : "var(--text-muted)" }}>Save letter</button>
      </div>
    );
  }

  if (mode === "experience") {
    const ready = fields.location || fields.activity;
    return (
      <div className="flex flex-col flex-1 min-h-0" data-testid="tc-manualform-experience">
        <Header />
        <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar space-y-4 pb-2">
          <div>
            <div className="text-[11px] uppercase tracking-wide mb-1" style={{ color: "var(--text-muted)" }}>Location</div>
            <LocationAutocomplete value={fields.location || ""} onChange={(v) => set("location", v)} testid="tc-exp-location" />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wide mb-1" style={{ color: "var(--text-muted)" }}>Activity</div>
            <textarea rows={2} placeholder="What did you do?" onChange={(e) => set("activity", e.target.value)} data-testid="tc-exp-activity"
              className="w-full rounded-xl px-3 py-2.5 text-sm outline-none" style={{ background: "var(--bg-surface)", border: "1px solid var(--border)", color: "var(--text-primary)" }} />
          </div>
          <ParticipantsInput onChange={(v) => set("participants", v)} />
          <div>
            <div className="text-[11px] uppercase tracking-wide mb-1" style={{ color: "var(--text-muted)" }}>What to remember</div>
            <MediaAnswer capSeconds={30} placeholder="Note about this experience…" onChange={(v) => set("content", v)} />
          </div>
        </div>
        <button onClick={() => save({ location: fields.location || "", activity: fields.activity || "", participants: fields.participants || "", content: fields.content || null })}
          disabled={!ready} data-testid="tc-manual-save"
          className="shrink-0 w-full rounded-xl py-3 mt-3 text-sm font-semibold" style={{ background: ready ? "var(--accent)" : "var(--overlay)", color: ready ? "#fff" : "var(--text-muted)" }}>Save experience</button>
      </div>
    );
  }

  if (mode === "greeting") {
    return <GreetingForm onExit={onExit} Header={Header} />;
  }

  // challenges / wisdom / favorites — Q&A with media
  const answered = cfg.qs.every(([k]) => fields[k]);
  return (
    <div className="flex flex-col flex-1 min-h-0" data-testid={`tc-manualform-${mode}`}>
      <Header />
      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar space-y-5 pb-2">
        {cfg.qs.map(([k, label]) => (
          <div key={k}>
            <div className="text-sm font-semibold mb-2">{label}</div>
            <MediaAnswer capSeconds={cfg.cap} onChange={(v) => set(k, v)} />
          </div>
        ))}
      </div>
      <button onClick={() => save(Object.fromEntries(cfg.qs.map(([k]) => [k, fields[k] || null])))} disabled={!answered} data-testid="tc-manual-save"
        className="shrink-0 w-full rounded-xl py-3 mt-3 text-sm font-semibold" style={{ background: answered ? "var(--accent)" : "var(--overlay)", color: answered ? "#fff" : "var(--text-muted)" }}>Save</button>
    </div>
  );
}

function GreetingForm({ onExit, Header }) {
  const [message, setMessage] = useState("");
  const [clip, setClip] = useState(null);        // recorded voice/video via MediaAnswer
  const [upload, setUpload] = useState(null);     // uploaded video file
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const fileRef = useRef(null);

  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUpload({ type: "video", media: data.path });
      toast.success("Video attached");
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const ready = message.trim() || (clip && clip.media) || (upload && upload.media);
  const save = async () => {
    setSaving(true);
    try {
      const data = { message: message.trim() || null };
      if (clip && clip.media) data.recording = clip;
      if (upload && upload.media) data.video = upload;
      await api.post("/timecapsule/entry", { mode: "greeting", title: (message.trim().slice(0, 40) || "Greeting"), data });
      setDone(true);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (done) return <DoneScreen label="Greeting" onExit={onExit} />;
  if (saving) return <div className="grid place-items-center flex-1"><Loader2 size={30} className="animate-spin gold-text" /></div>;

  return (
    <div className="flex flex-col flex-1 min-h-0" data-testid="tc-manualform-greeting">
      <Header />
      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar space-y-5 pb-2">
        <div>
          <div className="text-sm font-semibold mb-2">Type a message</div>
          <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={4} data-testid="tc-greeting-message"
            placeholder="Hello, dear family…"
            className="w-full rounded-xl px-4 py-3 text-sm outline-none resize-none" style={{ background: "var(--bg-surface)", border: "1px solid var(--border)", color: "var(--text-primary)", lineHeight: 1.7 }} />
        </div>
        <div>
          <div className="text-sm font-semibold mb-2">Add a voice note or record a video <span className="text-xs font-normal" style={{ color: "var(--text-muted)" }}>(optional)</span></div>
          <MediaAnswer capSeconds={30} placeholder="Or type your greeting…" onChange={setClip} />
        </div>
        <div>
          <div className="text-sm font-semibold mb-2">Upload a video <span className="text-xs font-normal" style={{ color: "var(--text-muted)" }}>(optional)</span></div>
          {upload ? (
            <div className="rounded-xl p-2" style={{ background: "var(--overlay)" }}>
              <video src={fileUrl(upload.media)} controls className="w-full rounded-lg max-h-48" />
              <button onClick={() => setUpload(null)} className="mt-2 inline-flex items-center gap-1.5 text-sm" style={{ color: "var(--accent)" }}><RotateCcw size={14} /> Remove</button>
            </div>
          ) : (
            <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="tc-greeting-upload-btn"
              className="w-full flex items-center justify-center gap-2 rounded-xl py-3 text-sm" style={{ background: "var(--overlay)", border: "1px solid var(--border)", color: "var(--text-secondary)" }}>
              {uploading ? <Loader2 size={15} className="animate-spin" /> : <Video size={15} />} Choose a video
            </button>
          )}
          <input ref={fileRef} type="file" accept="video/*" onChange={onUpload} className="hidden" data-testid="tc-greeting-upload-input" />
        </div>
      </div>
      <button onClick={save} disabled={!ready} data-testid="tc-manual-save"
        className="shrink-0 w-full rounded-xl py-3 mt-3 text-sm font-semibold" style={{ background: ready ? "var(--accent)" : "var(--overlay)", color: ready ? "#fff" : "var(--text-muted)" }}>Save greeting</button>
    </div>
  );
}

function ParticipantsInput({ onChange }) {
  const [text, setText] = useState("");
  const [members, setMembers] = useState([]);
  const [sugg, setSugg] = useState([]);
  useEffect(() => { api.get("/directory").then(({ data }) => setMembers(data.living || [])).catch(() => {}); }, []);
  const onType = (v) => {
    setText(v); onChange(v);
    const last = v.split(",").pop().trim().toLowerCase();
    setSugg(last ? members.filter((m) => (m.name || "").toLowerCase().includes(last)).slice(0, 4) : []);
  };
  const pick = (name) => {
    const parts = text.split(","); parts[parts.length - 1] = ` ${name}`;
    const nv = parts.join(",").replace(/^,\s*/, "");
    setText(nv); onChange(nv); setSugg([]);
  };
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide mb-1" style={{ color: "var(--text-muted)" }}>Participants</div>
      <input value={text} onChange={(e) => onType(e.target.value)} placeholder="Type names (members auto-suggest)…" data-testid="tc-exp-participants"
        className="w-full rounded-xl px-3 py-2.5 text-sm outline-none" style={{ background: "var(--bg-surface)", border: "1px solid var(--border)", color: "var(--text-primary)" }} />
      {sugg.length > 0 && (
        <div className="mt-1 flex flex-wrap gap-1.5">
          {sugg.map((m) => (
            <button key={m.id} onClick={() => pick(m.name)} data-testid={`tc-exp-suggest-${m.id}`}
              className="rounded-full px-2.5 py-1 text-xs" style={{ background: "color-mix(in srgb, var(--accent) 16%, transparent)", color: "var(--accent)" }}>{m.name}</button>
          ))}
        </div>
      )}
    </div>
  );
}

function BrowseCapsule({ userId, userName }) {
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState([]);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/timecapsule/user/${userId}`);
        setEntries(data.entries);
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
      finally { setLoading(false); }
    })();
  }, [userId]);

  if (loading) return <div className="grid place-items-center flex-1"><Loader2 size={28} className="animate-spin gold-text" /></div>;

  if (open) return (
    <div className="flex flex-col h-full" data-testid="tc-entry-view">
      <button onClick={() => setOpen(null)} className="shrink-0 flex items-center gap-2 text-sm mb-3" style={{ color: "var(--text-secondary)" }}>
        <ArrowLeft size={16} /> Back
      </button>
      <div className="font-serif-display text-2xl mb-3 shrink-0">{open.topic}</div>
      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar space-y-4 pb-2">
        {open.answers ? open.answers.map((a, i) => (
          <div key={i}>
            <div className="text-sm font-semibold mb-1">{a.question}</div>
            {a.type === "skipped" ? <div className="text-xs italic" style={{ color: "var(--text-muted)" }}>Skipped</div>
              : a.type === "video" ? <video src={fileUrl(a.media)} controls className="w-full rounded-lg max-h-56" />
              : a.type === "audio" ? <audio src={fileUrl(a.media)} controls className="w-full" />
              : <p className="text-sm whitespace-pre-wrap" style={{ color: "var(--text-secondary)" }}>{a.text}</p>}
          </div>
        )) : Object.entries(open.data || {}).filter(([k]) => k !== "title").map(([k, v]) => (
          <div key={k}>
            <div className="text-sm font-semibold mb-1 capitalize">{k}</div>
            {v && typeof v === "object" && v.media ? (
              v.type === "video" ? <video src={fileUrl(v.media)} controls className="w-full rounded-lg max-h-56" /> : <audio src={fileUrl(v.media)} controls className="w-full" />
            ) : v && typeof v === "object" ? (
              <p className="text-sm whitespace-pre-wrap" style={{ color: "var(--text-secondary)" }}>{v.text}</p>
            ) : (
              <p className="text-sm whitespace-pre-wrap" style={{ color: "var(--text-secondary)" }}>{String(v)}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="flex flex-col h-full" data-testid="tc-browse">
      <div className="flex items-center gap-2 mb-3 shrink-0">
        <Clock size={18} className="gold-text" />
        <div className="font-serif-display text-xl">{userName || "Member"}'s Time Capsule</div>
      </div>
      {entries.length === 0 ? (
        <p className="text-sm" style={{ color: "var(--text-muted)" }}>Nothing saved here yet.</p>
      ) : (
        <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar space-y-2">
          {entries.map((e) => (
            <button key={e.id} onClick={() => setOpen(e)} data-testid={`tc-entry-${e.id}`}
              className="w-full text-left rounded-xl px-4 py-3 card-surface">
              <div className="flex items-center gap-2">
                <BookOpen size={15} className="gold-text" />
                <span className="text-sm font-semibold">{e.topic}</span>
              </div>
              <div className="text-[11px] mt-0.5" style={{ color: "var(--text-muted)" }}>{e.answers ? `${e.answers.length} answers · Interview` : e.mode ? e.mode.charAt(0).toUpperCase() + e.mode.slice(1) : "Entry"}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------- Time Capsule Feed (filters + paywall + upgrade funnel) ----------------
const FEED_MODES = [["all", "All modes"], ["interview", "Interviews"], ["letters", "Letters"],
  ["experience", "Experience"], ["challenges", "Challenges"], ["wisdom", "Wisdom"], ["favorites", "Favorites"], ["greeting", "Greetings"]];

function tcTransferMedia(entry) {
  let path = null, kind = null;
  const lines = [];
  const take = (v) => { if (!path && v && typeof v === "object" && v.media) { path = v.media; kind = v.type; } };
  if (entry.answers) {
    entry.answers.forEach((a) => {
      if (a.type === "video" || a.type === "audio") take(a);
      if (a.text) lines.push(`**${a.question}**\n\n${a.text}`);
      else if (a.question) lines.push(`**${a.question}**`);
    });
  } else {
    Object.entries(entry.data || {}).forEach(([k, v]) => {
      if (k === "title") return;
      if (v && typeof v === "object" && v.media) { take(v); if (v.text) lines.push(`**${k}**\n\n${v.text}`); }
      else if (v && typeof v === "object") { if (v.text) lines.push(`**${k}**\n\n${v.text}`); }
      else if (v) lines.push(`**${k}**\n\n${String(v)}`);
    });
  }
  return { path, kind: kind || "pdf", title: entry.topic || "Time Capsule entry", source: "timecapsule", ref_id: entry.id, ref_collection: "timecapsule", body_md: lines.join("\n\n") || null };
}

function EntryView({ entry, onBack }) {
  return (
    <div className="flex flex-col h-full" data-testid="tc-entry-view">
      <div className="shrink-0 flex items-center justify-between mb-3 gap-2">
        <button onClick={onBack} className="flex items-center gap-2 text-sm" style={{ color: "var(--text-secondary)" }} data-testid="tc-entry-back">
          <ArrowLeft size={16} /> Back to feed
        </button>
        <MoveToVaultButton testid="tc-move-to-vault" label="Move to Secret Vault"
          className="flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold"
          media={tcTransferMedia(entry)} />
      </div>
      <div className="font-serif-display text-2xl mb-3 shrink-0">{entry.topic}</div>
      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar space-y-4 pb-2">
        {entry.answers ? entry.answers.map((a, i) => (
          <div key={i}>
            <div className="text-sm font-semibold mb-1">{a.question}</div>
            {a.type === "skipped" ? <div className="text-xs italic" style={{ color: "var(--text-muted)" }}>Skipped</div>
              : a.type === "video" ? <video src={fileUrl(a.media)} controls className="w-full rounded-lg max-h-56" />
              : a.type === "audio" ? <audio src={fileUrl(a.media)} controls className="w-full" />
              : <p className="text-sm whitespace-pre-wrap" style={{ color: "var(--text-secondary)" }}>{a.text}</p>}
          </div>
        )) : Object.entries(entry.data || {}).filter(([k]) => k !== "title").map(([k, v]) => (
          <div key={k}>
            <div className="text-sm font-semibold mb-1 capitalize">{k}</div>
            {v && typeof v === "object" && v.media ? (
              v.type === "video" ? <video src={fileUrl(v.media)} controls className="w-full rounded-lg max-h-56" /> : <audio src={fileUrl(v.media)} controls className="w-full" />
            ) : v && typeof v === "object" ? (
              <p className="text-sm whitespace-pre-wrap" style={{ color: "var(--text-secondary)" }}>{v.text}</p>
            ) : (
              <p className="text-sm whitespace-pre-wrap" style={{ color: "var(--text-secondary)" }}>{String(v)}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function CapsuleFeed({ lockedUserId, lockedUserName }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(lockedUserId || user?.id);
  const [mode, setMode] = useState("all");
  const [sort, setSort] = useState(lockedUserId ? "asc" : "desc");
  const [q, setQ] = useState("");
  const [showFunnel, setShowFunnel] = useState(false);

  const fetchFeed = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedUser) params.set("user_id", selectedUser);
      params.set("mode", mode);
      params.set("sort", sort);
      if (q.trim()) params.set("q", q.trim());
      const { data } = await api.get(`/timecapsule/feed?${params.toString()}`);
      setData(data);
      if (data.controls_locked) { setMode(data.mode); setSort(data.sort); }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [selectedUser, mode, sort, q]);

  useEffect(() => { const t = setTimeout(fetchFeed, q ? 350 : 0); return () => clearTimeout(t); }, [fetchFeed, q]);
  useEffect(() => { api.get("/timecapsule/members").then((r) => setMembers(r.data)).catch(() => {}); }, []);

  const locked = data?.controls_locked;   // non-legacy viewer -> filters/search locked
  const isSelf = data?.is_self;
  const selName = data?.target_user?.name || lockedUserName || "Member";
  const author = data?.target_user || { name: selName };

  const onUpgraded = async () => { setShowFunnel(false); window.dispatchEvent(new Event("fv-refresh-badge")); await fetchFeed(); };

  return (
    <div className="relative flex flex-col h-full" data-testid="tc-feed">
      <div className="flex items-center gap-2 mb-3 shrink-0">
        <Clock size={18} className="gold-text" />
        <div className="font-serif-display text-xl">{isSelf ? "My Time Capsule" : `${selName}'s Time Capsule`}</div>
      </div>

      {/* Search (Legacy only) */}
      <div className="shrink-0 mb-3">
        <div className="flex items-center gap-2 rounded-full px-4 py-2.5"
          style={{ background: "var(--overlay)", border: "1px solid var(--border)", opacity: locked ? 0.6 : 1 }}>
          {locked ? <Lock size={15} style={{ color: "var(--text-muted)" }} /> : <Search size={15} style={{ color: "var(--text-muted)" }} />}
          <input value={q} onChange={(e) => setQ(e.target.value)} disabled={locked} data-testid="tc-search"
            placeholder={locked ? "Search memories (Legacy only)" : "Search everything in this capsule…"}
            className="flex-1 bg-transparent text-sm outline-none disabled:cursor-not-allowed" style={{ color: "var(--text-primary)", fontSize: 16 }} />
        </div>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-3 gap-2 shrink-0 mb-3">
        <FeedSelect label="User" value={selectedUser} disabled={locked} onChange={setSelectedUser} testid="tc-filter-user"
          options={members.map((m) => [m.id, m.id === user?.id ? `${m.name} (You)` : m.name])} />
        <FeedSelect label="Mode" value={mode} disabled={locked} onChange={setMode} testid="tc-filter-mode" options={FEED_MODES} />
        <FeedSelect label="Date" value={sort} disabled={locked} onChange={setSort} testid="tc-filter-sort"
          options={[["desc", "Newest first"], ["asc", "Oldest first"]]} />
      </div>

      {locked && (
        <button onClick={() => setShowFunnel(true)} data-testid="tc-filters-locked-banner"
          className="shrink-0 mb-3 w-full flex items-center gap-2 rounded-xl px-3 py-2 text-left"
          style={{ background: "color-mix(in srgb, var(--accent) 10%, transparent)", border: "1px solid color-mix(in srgb, var(--accent) 35%, transparent)" }}>
          <Lock size={14} className="gold-text shrink-0" />
          <span className="text-xs" style={{ color: "var(--text-secondary)" }}>
            <b style={{ color: "var(--text-primary)" }}>Upgrade to Legacy</b> to search, browse every member and unlock all filters.
          </span>
        </button>
      )}

      {/* Feed */}
      {loading ? (
        <div className="grid place-items-center flex-1"><Loader2 size={26} className="animate-spin gold-text" /></div>
      ) : (data?.entries?.length || 0) === 0 ? (
        <p className="text-sm py-6 text-center" style={{ color: "var(--text-muted)" }}>{q ? "No memories match your search." : "Nothing saved here yet."}</p>
      ) : (
        <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar space-y-6 pb-4">
          {data.entries.map((e, i) => (
            <MemoryCard key={e.id} entry={e} author={author} index={i} />
          ))}

          {data.truncated && (
            <div className="relative rounded-2xl overflow-hidden mt-1" data-testid="tc-paywall">
              <div className="card-surface rounded-2xl px-4 py-8" style={{ filter: "blur(7px)", opacity: 0.5 }}>
                <div className="h-3 w-2/3 rounded mb-3" style={{ background: "var(--overlay)" }} />
                <div className="h-3 w-1/2 rounded mb-3" style={{ background: "var(--overlay)" }} />
                <div className="h-3 w-3/4 rounded" style={{ background: "var(--overlay)" }} />
              </div>
              <div className="absolute inset-0 grid place-items-center px-5 text-center"
                style={{ background: "linear-gradient(180deg, transparent, color-mix(in srgb, var(--bg-base) 90%, transparent) 50%)" }}>
                <div>
                  <div className="h-11 w-11 rounded-2xl grid place-items-center mx-auto mb-2" style={{ background: "color-mix(in srgb, var(--accent) 18%, transparent)" }}>
                    <Lock size={20} className="gold-text" />
                  </div>
                  <div className="font-serif-display text-xl">Want Access to Time Capsule?</div>
                  <p className="text-xs mt-1 mb-3" style={{ color: "var(--text-secondary)" }}>
                    {data.total - 3} more {data.total - 3 === 1 ? "entry is" : "entries are"} locked. Unlock everyone's full Time Capsule with Legacy.
                  </p>
                  <motion.button whileTap={{ scale: 0.97 }} onClick={() => setShowFunnel(true)} data-testid="tc-upgrade-btn"
                    className="rounded-xl px-6 py-3 font-semibold text-sm inline-flex items-center gap-2" style={{ background: "var(--accent)", color: "#fff" }}>
                    <Crown size={16} /> Upgrade to unlock
                  </motion.button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <AnimatePresence>
        {showFunnel && <UpgradeFunnel onClose={() => setShowFunnel(false)} onUpgraded={onUpgraded} />}
      </AnimatePresence>
    </div>
  );
}

function FeedSelect({ label, value, onChange, options, disabled, testid }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest mb-1 flex items-center gap-1" style={{ color: "var(--text-muted)" }}>
        {label}{disabled && <Lock size={9} />}
      </div>
      <div className="relative">
        <select value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled} data-testid={testid}
          className="w-full appearance-none rounded-xl pl-3 pr-7 py-2.5 text-xs font-medium outline-none disabled:opacity-60"
          style={{ background: "var(--overlay)", border: "1px solid var(--border)", color: "var(--text-primary)" }}>
          {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <ChevronDown size={14} className="absolute right-2 top-3 pointer-events-none" style={{ color: "var(--text-muted)" }} />
      </div>
    </div>
  );
}

function UpgradeFunnel({ onClose, onUpgraded }) {
  const { refreshUser } = useAuth();
  const [info, setInfo] = useState(null);
  const [billing, setBilling] = useState("monthly");
  const [step, setStep] = useState("benefits"); // benefits | checkout
  const [checkout, setCheckout] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { api.get("/subscription/upgrade-info").then((r) => setInfo(r.data)).catch(() => {}); }, []);

  const startCheckout = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/subscription/checkout", { tier: "legacy", billing, origin_url: window.location.origin });
      if (data.status === "active") { await refreshUser(); onUpgraded(); return; }
      setCheckout({ client_secret: data.client_secret, session_id: data.session_id });
      setStep("checkout");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  const onPaid = async () => { await refreshUser(); onUpgraded(); };

  const price = info?.legacy_price;
  const priceLabel = !price ? "" : billing === "monthly" ? `$${price.monthly.toFixed(2)}/mo` : `$${price.yearly.toFixed(2)}/yr`;

  return (
    <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 24 }}
      className="absolute inset-0 z-20 flex flex-col" style={{ background: "var(--bg-surface)" }} data-testid="tc-upgrade-funnel">
      <div className="flex items-center justify-between shrink-0 pb-3 mb-1" style={{ borderBottom: "1px solid var(--hairline)" }}>
        {step === "checkout"
          ? <button onClick={() => setStep("benefits")} className="flex items-center gap-1.5 text-sm" style={{ color: "var(--text-secondary)" }} data-testid="tc-funnel-back"><ArrowLeft size={16} /> Back</button>
          : <div className="flex items-center gap-2 gold-text"><Crown size={18} /><span className="text-xs uppercase tracking-widest">Legacy</span></div>}
        <button onClick={onClose} className="text-sm" style={{ color: "var(--text-muted)" }} data-testid="tc-funnel-close">Close</button>
      </div>

      {step === "benefits" ? (
        !info ? (
          <div className="grid place-items-center flex-1"><Loader2 className="animate-spin gold-text" /></div>
        ) : (
          <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar">
            <div className="font-serif-display text-3xl leading-tight mt-1">Unlock everyone's Time Capsule</div>
            <p className="text-sm mt-2" style={{ color: "var(--text-secondary)" }}>
              Here's what you gain moving from <b className="capitalize">{info.current_tier}</b> to <b className="gold-text">Legacy</b>:
            </p>
            <div className="space-y-2.5 mt-4">
              {info.benefits.map((b, i) => (
                <motion.div key={i} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.05 }}
                  className="flex items-start gap-3 card-surface rounded-2xl p-3.5" data-testid={`tc-benefit-${i}`}>
                  <div className="h-8 w-8 rounded-xl grid place-items-center shrink-0" style={{ background: "color-mix(in srgb, var(--accent) 16%, transparent)" }}>
                    <Check size={16} className="gold-text" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold">{b.title}</div>
                    <div className="text-xs" style={{ color: "var(--text-secondary)" }}>{b.detail}</div>
                  </div>
                </motion.div>
              ))}
            </div>

            <div className="flex gap-2 p-1 rounded-2xl mt-5" style={{ background: "var(--overlay)", border: "1px solid var(--border)" }}>
              {["monthly", "yearly"].map((b) => (
                <button key={b} onClick={() => setBilling(b)} data-testid={`tc-billing-${b}`}
                  className="flex-1 rounded-xl py-2 text-xs font-semibold transition-colors"
                  style={billing === b ? { background: "var(--accent)", color: "#fff" } : { color: "var(--text-secondary)" }}>
                  {b === "monthly" ? "Monthly" : "Yearly · save more"}
                </button>
              ))}
            </div>

            <motion.button whileTap={{ scale: 0.97 }} onClick={startCheckout} disabled={submitting} data-testid="tc-funnel-upgrade"
              className="w-full mt-4 mb-2 rounded-xl py-3.5 font-semibold text-sm flex items-center justify-center gap-2" style={{ background: "var(--accent)", color: "#fff" }}>
              {submitting ? <Loader2 size={16} className="animate-spin" /> : <Crown size={16} />} Upgrade to Legacy · {priceLabel}
            </motion.button>
          </div>
        )
      ) : (
        <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar pt-2">
          <EmbeddedCheckoutInline clientSecret={checkout?.client_secret} sessionId={checkout?.session_id} onPaid={onPaid} />
        </div>
      )}
    </motion.div>
  );
}


// ---------------- Feed cards ----------------
const MODE_META = {
  interview: { label: "Interview", icon: BookOpen, accent: "var(--accent)" },
  letters: { label: "Letter", icon: Mail, accent: "var(--accent-gold, #D4AF37)" },
  experience: { label: "Experience", icon: Map, accent: "#5FA8D3" },
  wisdom: { label: "Wisdom", icon: Quote, accent: "#C9A227" },
  challenges: { label: "Challenge", icon: Mountain, accent: "#B07BAC" },
  favorites: { label: "Favorite", icon: Heart, accent: "#E0685E" },
};

function agoShort(d) {
  try {
    const s = Math.floor((Date.now() - new Date(d).getTime()) / 1000);
    if (s < 60) return "just now";
    const m = Math.floor(s / 60); if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`;
    const days = Math.floor(h / 24); if (days < 30) return `${days}d ago`;
    return new Date(d).toLocaleDateString(undefined, { month: "short", year: "numeric" });
  } catch { return ""; }
}

function FeedAvatar({ name, photo }) {
  return photo
    ? <img src={fileUrl(photo)} alt="" className="h-10 w-10 rounded-full object-cover gold-border" />
    : <div className="h-10 w-10 rounded-full grid place-items-center gold-border" style={{ background: "var(--bg-elevated)" }}>
        <span className="font-serif-display text-base gold-text">{(name || "?").charAt(0).toUpperCase()}</span>
      </div>;
}

function MemoryCard({ entry, author, index }) {
  const meta = MODE_META[entry.mode] || (entry.answers ? MODE_META.interview : { label: entry.mode || "Memory", icon: FileText, accent: "var(--accent)" });
  const Icon = meta.icon;
  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(index * 0.06, 0.4) }}
      data-testid={`tc-card-${entry.id}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-2.5">
        <div className="flex items-center gap-2.5">
          <FeedAvatar name={author?.name} photo={author?.photo} />
          <div>
            <div className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>{author?.name}</div>
            <div className="text-[11px]" style={{ color: "var(--text-muted)" }}>{agoShort(entry.created_at)}</div>
          </div>
        </div>
        <span className="flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] uppercase tracking-wider"
          style={{ background: "var(--overlay)", border: "1px solid var(--border)", color: meta.accent }}>
          <Icon size={11} /> {meta.label}
        </span>
      </div>

      {/* Body */}
      {entry.answers ? <InterviewCard entry={entry} />
        : entry.mode === "letters" ? <LetterCard entry={entry} />
        : entry.mode === "experience" ? <ExperienceCard entry={entry} />
        : entry.mode === "wisdom" ? <WisdomCard entry={entry} />
        : entry.mode === "challenges" ? <ChallengeCard entry={entry} />
        : entry.mode === "favorites" ? <FavoriteCard entry={entry} />
        : <GenericCard entry={entry} />}

      {/* Footer: like + comments */}
      <div className="mt-2 pl-1 flex items-center gap-4">
        <LikeButton kind="timecapsule" id={entry.id} liked={entry.liked} count={entry.like_count} />
      </div>
      <CommentThread entryId={entry.id} initialCount={entry.comment_count || 0} />
    </motion.div>
  );
}

function LikeButton({ kind, id, liked: liked0, count: count0 }) {
  const [liked, setLiked] = useState(!!liked0);
  const [count, setCount] = useState(count0 || 0);
  const toggle = async () => {
    try { const { data } = await api.post("/likes/toggle", { kind, id }); setLiked(data.liked); setCount(data.like_count); } catch {}
  };
  return (
    <button onClick={toggle} data-testid={`tc-like-${id}`} className="flex items-center gap-1.5">
      <Heart size={15} fill={liked ? "#E0685E" : "none"} style={{ color: liked ? "#E0685E" : "var(--text-muted)" }} />
      <span className="text-[11px]" style={{ color: "var(--text-muted)" }}>{count}</span>
    </button>
  );
}

// Renders a value that may be a MediaAnswer object {type,text,media} or a plain string
function ValueBlock({ v, empty }) {
  if (v === null || v === undefined || v === "") return empty ? <p className="text-sm italic" style={{ color: "var(--text-muted)" }}>{empty}</p> : null;
  if (typeof v === "object" && v.media) {
    return v.type === "video"
      ? <VideoBlock src={v.media} />
      : <AudioPlayer src={v.media} />;
  }
  const text = typeof v === "object" ? v.text : v;
  return <p className="text-sm whitespace-pre-wrap leading-relaxed" style={{ color: "var(--text-secondary)" }}>{text}</p>;
}

function LetterCard({ entry }) {
  const [open, setOpen] = useState(false);
  const letter = entry.data?.letter || "";
  const title = entry.data?.title || entry.topic;
  return (
    <div className="rounded-2xl p-5" data-testid="tc-card-letter"
      style={{ background: "linear-gradient(160deg, #26221A, #1B1813)", borderTop: "2px solid var(--accent-gold, #D4AF37)", border: "1px solid rgba(212,175,55,0.18)" }}>
      {title && <div className="font-serif-display text-xl mb-2" style={{ color: "#EDE3C8" }}>{title}</div>}
      <p className={`font-serif-display leading-relaxed whitespace-pre-wrap ${open ? "" : "line-clamp-4"}`} style={{ color: "#DCD3BE", fontSize: 16 }}>{letter}</p>
      {letter.length > 160 && (
        <button onClick={() => setOpen((o) => !o)} data-testid="tc-letter-toggle" className="mt-2 text-xs font-semibold" style={{ color: "var(--accent-gold, #D4AF37)" }}>
          {open ? "Show less" : "Read letter"}
        </button>
      )}
    </div>
  );
}

function AudioPlayer({ src, caption }) {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [pct, setPct] = useState(0);
  const toggle = () => { const a = ref.current; if (!a) return; if (a.paused) { a.play(); setPlaying(true); } else { a.pause(); setPlaying(false); } };
  return (
    <div className="rounded-2xl p-3 flex items-center gap-3" data-testid="tc-audio" style={{ background: "var(--bg-elevated)", border: "1px solid var(--border)" }}>
      <audio ref={ref} src={fileUrl(src)} onTimeUpdate={(e) => setPct((e.target.currentTime / (e.target.duration || 1)) * 100)}
        onEnded={() => { setPlaying(false); setPct(0); }} className="hidden" />
      <button onClick={toggle} data-testid="tc-audio-toggle"
        className="h-12 w-12 rounded-full grid place-items-center shrink-0" style={{ background: "var(--accent)", color: "#fff", boxShadow: "0 0 18px rgba(124,131,255,0.3)" }}>
        {playing ? <Pause size={20} /> : <Play size={20} className="ml-0.5" />}
      </button>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 mb-1.5">
          <Mic size={12} className="gold-text" />
          <span className="text-xs" style={{ color: "var(--text-secondary)" }}>{caption || "Voice memo"}</span>
        </div>
        <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "var(--overlay)" }}>
          <div className="h-full rounded-full" style={{ width: `${pct}%`, background: "var(--accent)" }} />
        </div>
      </div>
    </div>
  );
}

function VideoBlock({ src, caption }) {
  return (
    <div className="rounded-2xl overflow-hidden" data-testid="tc-video" style={{ background: "#000", border: "1px solid var(--border)" }}>
      <video src={fileUrl(src)} controls className="w-full max-h-72 bg-black" />
      {caption && <div className="px-3 py-2 text-xs" style={{ color: "var(--text-secondary)", background: "var(--bg-elevated)" }}>{caption}</div>}
    </div>
  );
}

function MiniMap({ location }) {
  const [coords, setCoords] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (!location) { setFailed(true); return; }
    (async () => {
      try {
        const r = await fetch(`https://photon.komoot.io/api/?q=${encodeURIComponent(location)}&limit=1`);
        const d = await r.json();
        const f = (d.features || [])[0];
        if (f?.geometry?.coordinates) { const [lng, lat] = f.geometry.coordinates; setCoords({ lat, lng }); }
        else setFailed(true);
      } catch { setFailed(true); }
    })();
  }, [location]);

  if (failed || !coords) {
    return (
      <div className="h-32 grid place-items-center relative" style={{ background: "linear-gradient(135deg, #1a2733, #12202b)" }}>
        <div className="text-center">
          <MapPin size={22} className="mx-auto" style={{ color: "#5FA8D3" }} />
          <div className="text-xs mt-1" style={{ color: "var(--text-secondary)" }}>{location || "Somewhere special"}</div>
        </div>
      </div>
    );
  }
  const { lat, lng } = coords;
  const d = 0.02;
  const bbox = `${lng - d},${lat - d},${lng + d},${lat + d}`;
  return (
    <div className="h-32 relative overflow-hidden" data-testid="tc-map">
      <iframe title="map" className="w-full h-full" style={{ border: 0, filter: "saturate(0.9)" }}
        src={`https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${lat},${lng}`} />
      <div className="absolute inset-x-0 bottom-0 h-8 pointer-events-none" style={{ background: "linear-gradient(180deg, transparent, rgba(0,0,0,0.55))" }} />
      <div className="absolute inset-0 pointer-events-none grid place-items-center" style={{ paddingBottom: 18 }}>
        <MapPin size={26} style={{ color: "#E0685E", filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.6))" }} />
      </div>
    </div>
  );
}

function ExperienceCard({ entry }) {
  const d = entry.data || {};
  return (
    <div className="rounded-2xl overflow-hidden" data-testid="tc-card-experience" style={{ border: "1px solid var(--border)" }}>
      <MiniMap location={d.location} />
      <div className="p-4 space-y-3" style={{ background: "var(--bg-elevated)" }}>
        {d.location && <div className="flex items-center gap-1.5 text-xs" style={{ color: "#5FA8D3" }}><MapPin size={13} /> {d.location}</div>}
        {d.activity && <p className="text-sm whitespace-pre-wrap leading-relaxed" style={{ color: "var(--text-primary)" }}>{d.activity}</p>}
        {d.participants && <div className="flex items-center gap-1.5 text-xs" style={{ color: "var(--text-muted)" }}><Users size={12} /> {d.participants}</div>}
        <ValueBlock v={d.content} />
      </div>
    </div>
  );
}

function WisdomCard({ entry }) {
  const d = entry.data || {};
  const quote = (typeof d.quote === "object" ? d.quote?.text : d.quote) || (typeof d.wisdom === "object" ? d.wisdom?.text : d.wisdom) || entry.topic;
  const context = d.context || d.circumstance;
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-2xl p-5 relative overflow-hidden" data-testid="tc-card-wisdom"
      style={{ background: "var(--bg-surface)", borderLeft: "4px solid var(--accent-gold, #D4AF37)", border: "1px solid var(--border)" }}>
      <span className="absolute -top-6 left-1 font-serif-display pointer-events-none" style={{ fontSize: "7rem", color: "rgba(255,255,255,0.05)", lineHeight: 1 }}>“</span>
      <p className="font-serif-display italic relative z-10" style={{ color: "var(--text-primary)", fontSize: 22, lineHeight: 1.4 }}>{quote}</p>
      {context && (
        <>
          <button onClick={() => setOpen((o) => !o)} data-testid="tc-wisdom-toggle"
            className="mt-3 flex items-center gap-1 text-xs font-semibold" style={{ color: "var(--accent-gold, #D4AF37)" }}>
            {open ? "Hide context" : "Show context"} <ChevronDown size={14} style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />
          </button>
          <AnimatePresence>
            {open && (
              <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                <div className="pt-3">
                  <ValueBlock v={typeof context === "object" ? context : context} />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </>
      )}
    </div>
  );
}

function ChallengeCard({ entry }) {
  const d = entry.data || {};
  const rows = [["The Situation", d.situation], ["The Feelings", d.feelings], ["The Lesson", d.lessons]].filter(([, v]) => v);
  return (
    <div className="rounded-2xl p-5 space-y-4" data-testid="tc-card-challenges"
      style={{ background: "#08080c", border: "1px solid var(--border)", boxShadow: "inset 0 2px 12px rgba(0,0,0,0.4)" }}>
      <div className="flex items-center gap-2"><Mountain size={16} style={{ color: "#B07BAC" }} /><span className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>{entry.topic}</span></div>
      {rows.map(([label, v]) => (
        <div key={label}>
          <div className="uppercase tracking-widest text-[10px] mb-1" style={{ color: "#B07BAC" }}>{label}</div>
          <ValueBlock v={v} />
        </div>
      ))}
    </div>
  );
}

function FavoriteCard({ entry }) {
  const d = entry.data || {};
  const thing = (typeof d.thing === "object" ? d.thing?.text : d.thing) || entry.topic;
  return (
    <div className="rounded-2xl p-5" data-testid="tc-card-favorites"
      style={{ background: "linear-gradient(160deg, #2a1a1c, #1c1416)", border: "1px solid rgba(224,104,94,0.22)" }}>
      <div className="flex items-center gap-2 mb-2">
        <div className="h-8 w-8 rounded-full grid place-items-center" style={{ background: "rgba(224,104,94,0.18)" }}><Heart size={16} style={{ color: "#E0685E" }} /></div>
        <span className="font-serif-display text-lg" style={{ color: "#F4D9D6" }}>{thing}</span>
      </div>
      <ValueBlock v={d.why} empty="A cherished favorite." />
    </div>
  );
}

function InterviewCard({ entry }) {
  const [open, setOpen] = useState(false);
  const answers = (entry.answers || []).filter((a) => a.type !== "skipped");
  const preview = answers.slice(0, open ? answers.length : 1);
  return (
    <div className="rounded-2xl p-5 space-y-4" data-testid="tc-card-interview" style={{ background: "var(--bg-surface)", border: "1px solid var(--border)" }}>
      <div className="flex items-center gap-2"><BookOpen size={16} className="gold-text" /><span className="font-serif-display text-lg">{entry.topic}</span></div>
      {preview.map((a, i) => (
        <div key={i}>
          <div className="text-sm font-semibold mb-1.5" style={{ color: "var(--text-primary)" }}>{a.question}</div>
          {a.type === "video" ? <VideoBlock src={a.media} />
            : a.type === "audio" ? <AudioPlayer src={a.media} caption="Recorded answer" />
            : <p className="text-sm whitespace-pre-wrap leading-relaxed" style={{ color: "var(--text-secondary)" }}>{a.text}</p>}
        </div>
      ))}
      {answers.length > 1 && (
        <button onClick={() => setOpen((o) => !o)} data-testid="tc-interview-toggle" className="text-xs font-semibold gold-text">
          {open ? "Show less" : `View all ${answers.length} answers`}
        </button>
      )}
    </div>
  );
}

function CommentThread({ entryId, initialCount }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [comments, setComments] = useState(null);
  const [count, setCount] = useState(initialCount);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);

  const load = async () => {
    try { const { data } = await api.get(`/timecapsule/${entryId}/comments`); setComments(data); setCount(data.length); }
    catch { setComments([]); }
  };
  const toggle = () => { const n = !open; setOpen(n); if (n && comments === null) load(); };

  const send = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post(`/timecapsule/${entryId}/comments`, { content: text.trim() });
      setComments((c) => [...(c || []), data]); setCount((n) => n + 1); setText("");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  return (
    <div className="mt-2 pl-1">
      <button onClick={toggle} data-testid={`tc-comment-toggle-${entryId}`} className="flex items-center gap-1.5 text-xs" style={{ color: "var(--text-muted)" }}>
        <MessageCircle size={14} /> {count > 0 ? `${count} comment${count === 1 ? "" : "s"}` : "Comment"}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
            <div className="pt-3 space-y-2.5">
              {comments === null ? <Loader2 size={16} className="animate-spin gold-text" />
                : comments.length === 0 ? <p className="text-xs" style={{ color: "var(--text-muted)" }}>Be the first to comment.</p>
                : comments.map((c) => (
                  <div key={c.id} className="flex items-start gap-2" data-testid={`tc-comment-${c.id}`}>
                    <FeedAvatarSmall name={c.author?.name} />
                    <div className="rounded-2xl px-3 py-2 flex-1" style={{ background: "var(--overlay)" }}>
                      <div className="text-xs font-semibold" style={{ color: "var(--text-primary)" }}>{c.author?.name}</div>
                      <div className="text-sm" style={{ color: "var(--text-secondary)" }}>{c.content}</div>
                      <LikeButton kind="tc_comment" id={c.id} liked={c.liked} count={c.like_count} />
                    </div>
                  </div>
                ))}
              <div className="flex items-center gap-2 pt-1">
                <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
                  placeholder="Add a comment…" data-testid={`tc-comment-input-${entryId}`}
                  className="flex-1 rounded-full px-4 py-2.5 text-sm outline-none" style={{ background: "var(--bg-surface)", border: "1px solid var(--border)", color: "var(--text-primary)", fontSize: 16 }} />
                <button onClick={send} disabled={sending} data-testid={`tc-comment-send-${entryId}`}
                  className="h-9 w-9 rounded-full grid place-items-center shrink-0" style={{ background: "var(--accent)", color: "#fff" }}>
                  {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function FeedAvatarSmall({ name }) {
  return <div className="h-7 w-7 rounded-full grid place-items-center shrink-0" style={{ background: "var(--bg-elevated)", border: "1px solid var(--border)" }}>
    <span className="text-[11px] font-semibold gold-text">{(name || "?").charAt(0).toUpperCase()}</span>
  </div>;
}

function GenericCard({ entry }) {
  return (
    <div className="rounded-2xl p-4 card-surface" data-testid="tc-card-generic">
      <div className="font-serif-display text-lg mb-2">{entry.topic}</div>
      {Object.entries(entry.data || {}).filter(([k]) => k !== "title").map(([k, v]) => (
        <div key={k} className="mb-2">
          <div className="text-xs font-semibold capitalize mb-1" style={{ color: "var(--text-primary)" }}>{k}</div>
          <ValueBlock v={v} />
        </div>
      ))}
    </div>
  );
}

